import React, { useEffect, useState } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Plus, Edit2, Trash2, Search, Loader2, X, Building2 } from 'lucide-react';
import api from '@/utils/api';
import { showSuccess, showError } from '@/utils/toast';

const Branches = () => {
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingBranch, setEditingBranch] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    name: '', code: '', address: '', city: '', state: '', pin_code: '',
    contact_person: '', contact_phone: '', contact_email: '',
  });

  useEffect(() => { fetchBranches(); }, []);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
    } catch (error) {
      showError('Failed to fetch branches');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (branch = null) => {
    if (branch) {
      setEditingBranch(branch);
      setFormData({ ...branch });
    } else {
      setEditingBranch(null);
      setFormData({ name: '', code: '', address: '', city: '', state: '', pin_code: '', contact_person: '', contact_phone: '', contact_email: '' });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editingBranch) {
        const { code, ...updateData } = formData;
        await api.put(`/branches/${editingBranch.id}`, updateData);
        showSuccess('Branch updated successfully');
      } else {
        await api.post('/branches', formData);
        showSuccess('Branch created successfully');
      }
      setShowModal(false);
      fetchBranches();
    } catch (error) {
      showError(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this branch?')) return;
    try {
      await api.delete(`/branches/${id}`);
      showSuccess('Branch deleted successfully');
      fetchBranches();
    } catch (error) {
      showError('Failed to delete branch');
    }
  };

  const filtered = branches.filter((b) =>
    b.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    b.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2">Branches</h1>
            <p className="text-muted-foreground">Manage branch offices and locations</p>
          </div>
          <button data-testid="add-branch-button" onClick={() => handleOpenModal()} className="mt-4 md:mt-0 flex items-center gap-2 px-4 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-medium rounded-lg transition-colors">
            <Plus size={20} />
            Add Branch
          </button>
        </div>

        <div className="bg-card border border-border rounded-xl p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={20} />
            <input type="text" data-testid="search-branches" placeholder="Search branches..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full pl-10 pr-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.length === 0 ? (
            <div className="col-span-3 text-center py-12 text-muted-foreground">No branches found</div>
          ) : (
            filtered.map((branch) => (
              <div key={branch.id} data-testid={`branch-card-${branch.id}`} className="bg-card border border-border rounded-xl p-6 hover:shadow-lg transition-all">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="p-3 rounded-lg bg-primary/10">
                      <Building2 className="text-primary" size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-foreground">{branch.name}</h3>
                      <p className="text-xs text-muted-foreground">Code: {branch.code}</p>
                    </div>
                  </div>
                </div>
                <div className="space-y-2 mb-4 text-sm">
                  <p className="text-muted-foreground">{branch.address}</p>
                  <p className="text-foreground">{branch.city}, {branch.state} - {branch.pin_code}</p>
                  <div className="pt-2 border-t border-border space-y-1">
                    <p className="text-foreground"><span className="text-muted-foreground">Manager:</span> {branch.contact_person}</p>
                    <p className="text-foreground"><span className="text-muted-foreground">Phone:</span> {branch.contact_phone}</p>
                    <p className="text-foreground text-xs"><span className="text-muted-foreground">Email:</span> {branch.contact_email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 pt-4 border-t border-border">
                  <button data-testid={`edit-branch-${branch.id}`} onClick={() => handleOpenModal(branch)} className="flex-1 flex items-center justify-center gap-2 px-3 py-2 border border-border rounded-lg hover:bg-muted transition-colors">
                    <Edit2 size={16} /> Edit
                  </button>
                  <button data-testid={`delete-branch-${branch.id}`} onClick={() => handleDelete(branch.id)} className="p-2 rounded-lg hover:bg-destructive/10 transition-colors">
                    <Trash2 size={16} className="text-destructive" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {showModal && (
          <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
            <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold">{editingBranch ? 'Edit Branch' : 'Add New Branch'}</h2>
                <button onClick={() => setShowModal(false)} className="p-1 hover:bg-muted rounded"><X size={20} /></button>
              </div>
              <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Branch Name *</label>
                  <input type="text" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Branch Code *</label>
                  <input type="text" required disabled={!!editingBranch} value={formData.code} onChange={(e) => setFormData({ ...formData, code: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none disabled:opacity-50" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Address *</label>
                  <input type="text" required value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">City *</label>
                  <input type="text" required value={formData.city} onChange={(e) => setFormData({ ...formData, city: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">State *</label>
                  <input type="text" required value={formData.state} onChange={(e) => setFormData({ ...formData, state: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">PIN Code *</label>
                  <input type="text" required value={formData.pin_code} onChange={(e) => setFormData({ ...formData, pin_code: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Manager Name *</label>
                  <input type="text" required value={formData.contact_person} onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Contact Phone *</label>
                  <input type="tel" required value={formData.contact_phone} onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2">Contact Email *</label>
                  <input type="email" required value={formData.contact_email} onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })} className="w-full px-4 py-3 rounded-lg border border-border bg-background focus:ring-2 focus:ring-primary/50 outline-none" />
                </div>
                <div className="md:col-span-2 flex gap-3 pt-4">
                  <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-3 border border-border rounded-lg hover:bg-accent">Cancel</button>
                  <button type="submit" disabled={submitting} data-testid="submit-branch" className="flex-1 px-4 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50 flex items-center justify-center gap-2">
                    {submitting && <Loader2 className="animate-spin" size={18} />}
                    {editingBranch ? 'Update' : 'Create'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default Branches;
